import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Users, FileText, Pencil, Trash2, Plus, Eye, Heart, Gift, DollarSign, Package, Network } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import BlogEditor from "../components/BlogEditor";
import ProductsManager from "../components/ProductsManager";
import CatalogBrowser from "../components/CatalogBrowser";

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return "—"; }
}

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("stats");

  useEffect(() => {
    if (user === false) navigate("/login");
    if (user && typeof user === "object" && user.role !== "admin") {
      toast.error("Admins only.");
      navigate("/dashboard");
    }
  }, [user, navigate]);

  if (!user || typeof user !== "object" || user.role !== "admin") {
    return <div className="container-x py-16"><p className="font-manrope text-ink-soft animate-soft-pulse">Checking access…</p></div>;
  }

  return (
    <div className="container-x py-12 lg:py-16" data-testid="admin-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <span className="tag-pill">Admin</span>
          <h1 className="mt-3 font-outfit text-3xl lg:text-4xl font-medium text-ink tracking-tight">
            Control room
          </h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setTab("stats")}
            className={`rounded-full px-4 py-2 text-sm font-medium border ${tab === "stats" ? "bg-brand text-white border-brand" : "bg-white text-ink-soft border-[#EBE2E8]"}`}
            data-testid="admin-tab-stats"
          >
            Stats
          </button>
          <button
            onClick={() => setTab("blog")}
            className={`rounded-full px-4 py-2 text-sm font-medium border ${tab === "blog" ? "bg-brand text-white border-brand" : "bg-white text-ink-soft border-[#EBE2E8]"}`}
            data-testid="admin-tab-blog"
          >
            Blog
          </button>
          <button
            onClick={() => setTab("products")}
            className={`rounded-full px-4 py-2 text-sm font-medium border ${tab === "products" ? "bg-brand text-white border-brand" : "bg-white text-ink-soft border-[#EBE2E8]"}`}
            data-testid="admin-tab-products"
          >
            <span className="inline-flex items-center gap-1.5"><Package className="h-3.5 w-3.5" /> Products</span>
          </button>
          <button
            onClick={() => setTab("catalog")}
            className={`rounded-full px-4 py-2 text-sm font-medium border ${tab === "catalog" ? "bg-brand text-white border-brand" : "bg-white text-ink-soft border-[#EBE2E8]"}`}
            data-testid="admin-tab-catalog"
          >
            <span className="inline-flex items-center gap-1.5"><Network className="h-3.5 w-3.5" /> Catalog</span>
          </button>
        </div>
      </div>

      <div className="mt-8">
        {tab === "stats" && <Stats />}
        {tab === "blog" && <BlogManager />}
        {tab === "products" && <ProductsManager />}
        {tab === "catalog" && <CatalogBrowser />}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sublabel }) {
  return (
    <div className="card-soft p-6">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-full bg-brand-soft flex items-center justify-center">
          <Icon className="h-5 w-5 text-brand" strokeWidth={1.6} />
        </div>
        <div>
          <p className="font-manrope text-xs text-ink-soft uppercase tracking-wider">{label}</p>
          <p className="font-outfit text-3xl font-medium text-ink leading-tight">{value}</p>
          {sublabel && <p className="font-manrope text-xs text-ink-soft mt-1">{sublabel}</p>}
        </div>
      </div>
    </div>
  );
}

function Stats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/stats");
        setStats(data);
      } catch (e) {
        toast.error("Couldn't load stats.");
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <p className="font-manrope text-ink-soft animate-soft-pulse">Loading stats…</p>;
  if (!stats) return null;

  return (
    <div data-testid="admin-stats">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard icon={Users} label="Users" value={stats.users.total} sublabel={`+${stats.users.signups_last_7d} in last 7 days`} />
        <StatCard icon={Heart} label="Registries" value={stats.registries.total} sublabel={`${stats.registries.public} public · ${stats.registries.private} private`} />
        <StatCard icon={Gift} label="Items" value={stats.items.total} sublabel={`${stats.items.purchased} gifted · ${stats.items.claimed} claimed`} />
        <StatCard icon={DollarSign} label="Contributions" value={`$${stats.contributions.total_usd.toFixed(0)}`} sublabel={`${stats.contributions.count} contributions`} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-5">
        <StatCard icon={Heart} label="Thank-yous" value={stats.engagement.thank_yous} />
        <StatCard icon={FileText} label="Updates" value={stats.engagement.updates} />
        <StatCard icon={FileText} label="Blog posts" value={stats.blog.total} sublabel={`${stats.blog.published} published · ${stats.blog.drafts} drafts`} />
      </div>

      <div className="mt-10 card-soft p-6">
        <h3 className="font-outfit text-xl font-medium text-ink">Recent signups</h3>
        <div className="mt-4 divide-y divide-[#EBE2E8]">
          {stats.recent_users.map((u) => (
            <div key={u.email} className="py-3 flex items-center justify-between gap-3" data-testid={`admin-user-${u.email}`}>
              <div>
                <p className="font-manrope text-sm text-ink">{u.display_name || "—"}</p>
                <p className="font-manrope text-xs text-ink-soft">{u.email}</p>
              </div>
              <div className="flex items-center gap-3">
                {u.role === "admin" && <span className="tag-pill text-xs">admin</span>}
                <span className="font-manrope text-xs text-ink-soft">{formatDate(u.created_at)}</span>
              </div>
            </div>
          ))}
          {stats.recent_users.length === 0 && (
            <p className="font-manrope text-sm text-ink-soft py-3">No users yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | post_object

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/blog/posts");
      setPosts(data);
    } catch (e) {
      toast.error("Couldn't load posts.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/admin/blog/posts/${id}`);
      setPosts(posts.filter((p) => p.id !== id));
      toast.success("Deleted.");
    } catch (e) {
      toast.error("Couldn't delete.");
    }
  };

  if (editing) {
    return <BlogEditor post={editing === "new" ? null : editing} onBack={() => { setEditing(null); load(); }} />;
  }

  return (
    <div data-testid="admin-blog-manager">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="font-manrope text-sm text-ink-soft">
          {posts.length} {posts.length === 1 ? "post" : "posts"} total
        </p>
        <button onClick={() => setEditing("new")} className="btn-primary inline-flex items-center gap-2" data-testid="admin-blog-new-btn">
          <Plus className="h-4 w-4" /> New post
        </button>
      </div>

      {loading ? (
        <p className="mt-8 font-manrope text-ink-soft animate-soft-pulse">Loading posts…</p>
      ) : posts.length === 0 ? (
        <div className="mt-8 card-soft p-10 text-center" data-testid="admin-blog-empty">
          <FileText className="h-8 w-8 text-brand mx-auto mb-3 opacity-40" />
          <p className="font-outfit text-lg text-ink">No posts yet.</p>
          <p className="font-manrope text-sm text-ink-soft mt-2">
            Ship an SEO/LLMO post to start ranking.
          </p>
        </div>
      ) : (
        <div className="mt-8 grid gap-4">
          {posts.map((p) => (
            <div key={p.id} className="card-soft p-5 flex items-center gap-4" data-testid={`admin-post-${p.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`tag-pill text-xs ${p.status === "published" ? "!bg-[#E9F6EE] !text-[#2D6A4F]" : "!bg-[#FFF1D6] !text-[#8A5A00]"}`}>
                    {p.status}
                  </span>
                  {p.tags?.slice(0, 3).map((t) => (
                    <span key={t} className="tag-pill text-xs">#{t}</span>
                  ))}
                </div>
                <h3 className="mt-2 font-outfit text-lg font-medium text-ink truncate">{p.title}</h3>
                <p className="font-manrope text-xs text-ink-soft mt-1">
                  /blog/{p.slug} · updated {formatDate(p.updated_at)}
                </p>
              </div>
              <div className="flex gap-1 shrink-0">
                {p.status === "published" && (
                  <Link to={`/blog/${p.slug}`} className="p-2 rounded-full hover:bg-brand-soft" title="View" data-testid={`admin-post-view-${p.id}`}>
                    <Eye className="h-4 w-4 text-ink-soft" />
                  </Link>
                )}
                <button onClick={() => setEditing(p)} className="p-2 rounded-full hover:bg-brand-soft" title="Edit" data-testid={`admin-post-edit-${p.id}`}>
                  <Pencil className="h-4 w-4 text-ink-soft" />
                </button>
                <button onClick={() => remove(p.id)} className="p-2 rounded-full hover:bg-red-50" title="Delete" data-testid={`admin-post-delete-${p.id}`}>
                  <Trash2 className="h-4 w-4 text-red-600" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

